import type { CalculationBreakdown } from 'src/drheranca/services/partilhaTotalLiquidoBreakdown';
import { getTotalLiquidoBreakdown } from 'src/drheranca/services/partilhaTotalLiquidoBreakdown';
import { CalculosInventario, Inventario, PartilhaResultado } from 'src/drheranca/types';
import {
  buildFamilyMapFromInventario,
  filterPartilhasVivas,
} from 'src/drheranca/services/partilhaPersonFilters';
import { ExcelCellValue } from '../types';

export const CALCULO_PESSOA_COLS = 3;
export const CALCULO_PESSOA_GAP = 1;
export const CALCULO_PESSOA_BLOCK = CALCULO_PESSOA_COLS + CALCULO_PESSOA_GAP;

export function formatBrlForMemo(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function breakdownToBlockRows(breakdown: CalculationBreakdown): ExcelCellValue[][] {
  const rows: ExcelCellValue[][] = [[breakdown.title, null, null]];
  rows.push(['Descrição', 'Memória de cálculo', 'Valor (R$)']);

  breakdown.rows.forEach((r) => {
    if (r.isOperator) {
      rows.push([r.label, null, null]);
      return;
    }
    const value =
      r.value === '' || r.value == null
        ? null
        : typeof r.value === 'number'
          ? r.value
          : r.value;
    rows.push([r.label, r.memo ?? null, value]);
  });

  if (breakdown.totalLabel != null && breakdown.totalValue != null) {
    rows.push([breakdown.totalLabel, null, breakdown.totalValue]);
  }
  if (breakdown.note) {
    rows.push([breakdown.note, null, null]);
  }
  return rows;
}

export function buildCalculoPorPessoaExcelRows(
  inventario: Inventario,
  calculos: CalculosInventario,
  custosTributosPorPessoa: Record<
    string,
    { registroImoveis: number; honorariosAdvocaticios: number }
  >,
): { matrix: ExcelCellValue[][]; numPeople: number } | null {
  const familyMap = buildFamilyMapFromInventario(inventario);
  const decujusId = inventario.decujus?.id?.toString() || '';
  const partilhas = filterPartilhasVivas(
    calculos.partilhaPorHerdeiro || [],
    familyMap,
    decujusId,
  );
  if (partilhas.length === 0) return null;

  const blocks = partilhas.map((p) =>
    breakdownToBlockRows(
      getTotalLiquidoBreakdown({
        partilha: p,
        calculos,
        custosTributosPorPessoa,
        formatCurrency: formatBrlForMemo,
        partilhas: calculos.partilhaPorHerdeiro || [],
      }),
    ),
  );

  const maxRows = Math.max(...blocks.map((b) => b.length));
  const matrix: ExcelCellValue[][] = [];

  for (let r = 0; r < maxRows; r += 1) {
    const row: ExcelCellValue[] = [];
    blocks.forEach((block, blockIdx) => {
      if (blockIdx > 0) row.push(null);
      const blockRow = block[r] ?? [null, null, null];
      row.push(...blockRow);
    });
    matrix.push(row);
  }

  return { matrix, numPeople: partilhas.length };
}

export function getCalculoPorPessoaBreakdowns(
  inventario: Inventario,
  calculos: CalculosInventario,
  custosTributosPorPessoa: Record<
    string,
    { registroImoveis: number; honorariosAdvocaticios: number }
  >,
  formatCurrency: (v: number) => string,
): Array<{ partilha: PartilhaResultado; breakdown: CalculationBreakdown }> {
  const familyMap = buildFamilyMapFromInventario(inventario);
  const decujusId = inventario.decujus?.id?.toString() || '';
  const partilhas = filterPartilhasVivas(
    calculos.partilhaPorHerdeiro || [],
    familyMap,
    decujusId,
  );
  const todasPartilhas = calculos.partilhaPorHerdeiro || [];
  return partilhas.map((p) => ({
    partilha: p,
    breakdown: getTotalLiquidoBreakdown({
      partilha: p,
      calculos,
      custosTributosPorPessoa,
      formatCurrency,
      partilhas: todasPartilhas,
    }),
  }));
}
